import React from 'react';
import { FiCheckCircle, FiShield, FiUsers, FiBook } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import settings from '../config/settings';
import SEO from '../components/SEO';

const Terms = () => {
  const lastUpdated = "February 2026";

  return (
    <div className="animate-fadeIn">
      <SEO page="terms" />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 pt-32 pb-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs in Hero */}
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 mb-6" data-aos="fade-down">
            <a href="/" className="hover:text-emerald-600 transition-colors">Home</a>
            <span className="text-gray-400">/</span>
            <span className="text-emerald-600 font-medium">Terms & Conditions</span>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <div className="w-16 h-16 bg-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg" data-aos="fade-up">
              <SafeIcon icon={FiShield} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" data-aos="fade-up" data-aos-delay="100">
              Terms & <span className="text-emerald-600">Conditions</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-aos="fade-up" data-aos-delay="200">
              Please read these terms carefully before enrolling your child at {settings.siteName}
            </p>
            <div className="mt-6 inline-flex items-center space-x-2 bg-white px-6 py-3 rounded-full shadow-sm" data-aos="fade-up" data-aos-delay="300">
              <span className="text-sm text-gray-500">Last updated:</span>
              <span className="text-sm font-semibold text-emerald-600">{lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Overview Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="bg-emerald-50 rounded-2xl p-6 text-center" data-aos="fade-up">
              <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900">Admission</h3>
              <p className="text-sm text-gray-600 mt-2">Enrollment policies</p>
            </div>
            <div className="bg-blue-50 rounded-2xl p-6 text-center" data-aos="fade-up" data-aos-delay="100">
              <SafeIcon icon={FiUsers} className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900">Parent Rights</h3>
              <p className="text-sm text-gray-600 mt-2">Your responsibilities</p>
            </div>
            <div className="bg-purple-50 rounded-2xl p-6 text-center" data-aos="fade-up" data-aos-delay="200">
              <SafeIcon icon={FiShield} className="w-10 h-10 text-purple-600 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900">Safety First</h3>
              <p className="text-sm text-gray-600 mt-2">Child protection</p>
            </div>
            <div className="bg-orange-50 rounded-2xl p-6 text-center" data-aos="fade-up" data-aos-delay="300">
              <SafeIcon icon={FiBook} className="w-10 h-10 text-orange-600 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900">Policies</h3>
              <p className="text-sm text-gray-600 mt-2">School guidelines</p>
            </div>
          </div>

          {/* Terms Content */}
          <div className="max-w-4xl mx-auto">
            {/* Section 1 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">1</span>
                Acceptance of Terms
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  By enrolling your child at {settings.siteName}, you acknowledge that you have read, understood, and agree to comply with these Terms & Conditions. These terms constitute a legally binding agreement between the parent/guardian and the school.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify these terms at any time. Parents will be notified of any significant changes via email or in-person communication. Continued enrollment after modifications constitutes acceptance of the revised terms.
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">2</span>
                Admission & Enrollment
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">2.1 Admission Requirements</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Completed admission form with accurate information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Child's birth certificate and age verification documents</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Medical records including immunization history</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Recent passport-sized photographs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Payment of admission fee and first month's tuition</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">2.2 Enrollment Confirmation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Enrollment is confirmed only upon receipt of all required documents and payment of applicable fees. The school reserves the right to deny admission without stating reasons.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">3</span>
                Fee Structure & Payment
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3.1 Fee Payment Terms</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Tuition fees are due on or before the 5th of each month</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Late payments will incur a penalty of 2% per week</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Admission and registration fees are non-refundable</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Parents must provide 30 days' written notice for withdrawal</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Fee once paid will not be refunded under any circumstances</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <p className="text-sm text-amber-900 font-medium">
                    ⚠️ Important: Failure to pay fees for two consecutive months may result in suspension or termination of enrollment.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">4</span>
                Attendance & Timings
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">4.1 School Timings</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Regular school hours: {settings.hours.weekdays}
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Parents must ensure punctual drop-off and pick-up</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Late pick-ups beyond 30 minutes will incur additional charges</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Children will only be released to authorized guardians listed in records</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">4.2 Absences & Leave</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Parents must inform the school by 9:00 AM if their child will be absent. Extended absences (more than 5 consecutive days) require prior written approval from the school administration.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">5</span>
                Health & Safety
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">5.1 Medical Information</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Parents must disclose all allergies, medical conditions, and special needs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Updated emergency contact information must be provided</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Children showing symptoms of illness should not be sent to school</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">5.2 Medical Emergencies</h3>
                  <p className="text-gray-700 leading-relaxed">
                    In case of medical emergencies, the school will attempt to contact parents immediately. If unreachable, the school is authorized to seek necessary medical treatment. All medical expenses will be borne by parents/guardians.
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <p className="text-sm text-blue-900 font-medium">
                    ℹ️ First Aid: Basic first aid will be administered by trained staff for minor injuries. Parents will be notified of any incidents.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 6 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">6</span>
                Code of Conduct
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">6.1 Student Behavior</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    All students are expected to maintain respectful behavior towards staff and fellow students. The school employs positive reinforcement techniques and does not use corporal punishment.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">6.2 Parent Cooperation</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Parents must maintain respectful communication with staff</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Scheduled parent-teacher meetings are mandatory</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>School policies and procedures must be respected</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 7 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">7</span>
                Photography & Media
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  The school may photograph or video record children during activities for educational purposes, newsletters, website, or social media. Parents who do not wish their child to be photographed must submit written notification during admission.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Parents may photograph their own child during school events but must not publish photos including other children without proper consent.
                </p>
              </div>
            </div>

            {/* Section 8 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">8</span>
                Liability & Insurance
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  While the school takes every precaution to ensure child safety, we are not liable for accidents, injuries, or loss of personal belongings. Parents are advised to insure their child's belongings separately.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  The school maintains comprehensive liability insurance. However, this does not cover pre-existing medical conditions or injuries resulting from willful negligence by the child.
                </p>
              </div>
            </div>

            {/* Section 9 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">9</span>
                Termination of Enrollment
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">9.1 By Parents</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Parents may withdraw their child by providing 30 days' written notice. Fees paid for the current month are non-refundable.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">9.2 By School</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The school reserves the right to terminate enrollment if:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Fee payments are consistently delayed or unpaid</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>Parents fail to comply with school policies</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>The child's needs exceed the school's capabilities</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2 mt-1">•</span>
                      <span>There is persistent disruptive behavior affecting other children</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 10 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">10</span>
                Dispute Resolution
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Any disputes arising from these terms will first be attempted to be resolved through mediation. If mediation fails, the matter will be subject to the jurisdiction of courts in Hyderabad, India.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Parents are encouraged to communicate concerns directly with the school administration before seeking legal recourse.
                </p>
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl p-10 text-center text-white" data-aos="fade-up">
              <h3 className="text-2xl font-bold mb-4">Questions About Our Terms?</h3>
              <p className="text-emerald-50 mb-6 max-w-2xl mx-auto">
                If you have any questions or need clarification regarding these terms and conditions, please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="bg-white text-emerald-600 px-8 py-3 rounded-full font-semibold hover:bg-emerald-50 transition-colors"
                >
                  Contact Us
                </a>
                <a
                  href={`mailto:${settings.email}`}
                  className="bg-emerald-700 text-white px-8 py-3 rounded-full font-semibold hover:bg-emerald-800 transition-colors"
                >
                  Email: {settings.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
